import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2Icon, AlertCircleIcon, SendIcon } from 'lucide-react';
import emailjs from '@emailjs/browser';
interface FormData {
  nombre: string;
  email: string;
  telefono: string;
  salon: string;
  instagram: string;
  website: string;
  clientesSemana: string;
  metaClientes: string;
  publicidadAntes: string;
  inversionMensual: string;
}
const initialFormData: FormData = {
  nombre: '',
  email: '',
  telefono: '',
  salon: '',
  instagram: '',
  website: '',
  clientesSemana: '',
  metaClientes: '',
  publicidadAntes: '',
  inversionMensual: ''
};
const fadeUpVariants = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
const inputClasses = 'w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500 text-sm md:text-base focus:outline-none focus:border-gold/60 focus:ring-1 focus:ring-gold/30 transition-colors';
const selectClasses = 'w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-4 py-3 text-white text-sm md:text-base focus:outline-none focus:border-gold/60 focus:ring-1 focus:ring-gold/30 transition-colors appearance-none cursor-pointer';
const labelClasses = 'block text-gray-300 text-sm font-medium mb-1.5';
export function ContactForm() {
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('sending');
    try {
      await emailjs.send('service_ld498wo', 'template_ysz3gxn', {
        from_name: formData.nombre,
        from_email: formData.email,
        phone: formData.telefono,
        salon_name: formData.salon,
        instagram: formData.instagram,
        website: formData.website,
        clients_per_week: formData.clientesSemana,
        client_goal: formData.metaClientes,
        previous_ads: formData.publicidadAntes,
        monthly_investment: formData.inversionMensual,
        message: `Nuevo lead de Beauty Salon Landing:\n\nNombre: ${formData.nombre}\nEmail: ${formData.email}\nTeléfono: ${formData.telefono}\nSalón: ${formData.salon}\nInstagram: ${formData.instagram}\nWebsite: ${formData.website}\nClientes/semana: ${formData.clientesSemana}\nMeta clientes nuevos: ${formData.metaClientes}\nPublicidad previa: ${formData.publicidadAntes}\nInversión mensual: ${formData.inversionMensual}`
      }, '0SqCHVYKMPHE_MNQM');
      setStatus('success');
      setFormData(initialFormData);
    } catch {
      setStatus('error');
    }
  };
  return <section id="contacto" className="py-14 md:py-24 px-5 md:px-8 bg-carbon" data-id="element-1">
      <div className="max-w-3xl mx-auto" data-id="element-2">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-80px'
      }} variants={fadeUpVariants} data-id="element-3">
          <div className="text-center mb-10 md:mb-12" data-id="element-4">
            <h2 className="text-3xl md:text-4xl font-bold text-gold mb-4" data-id="element-5">
              Solicita Tu Diagnóstico Gratuito
            </h2>
            <p className="text-gray-400 text-base md:text-lg max-w-xl mx-auto" data-id="element-6">
              Completa el formulario y un especialista analizará tu situación en
              menos de 24 horas.
            </p>
          </div>

          {status === 'success' ? <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="bg-surface border border-emerald/30 rounded-2xl p-8 md:p-12 text-center" data-id="element-7">
              <div className="w-16 h-16 bg-emerald/10 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-8">
                <CheckCircle2Icon className="w-8 h-8 text-emerald" data-id="element-9" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-3" data-id="element-10">
                ¡Recibimos tu información!
              </h3>
              <p className="text-gray-400 text-lg" data-id="element-11">
                Te contactaremos en menos de 24 horas.
              </p>
            </motion.div> : <form onSubmit={handleSubmit} className="bg-surface border border-white/5 rounded-2xl p-6 md:p-10 space-y-5" data-id="element-12">
              {/* Row 1: Nombre + Email */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-13">
                <div data-id="element-14">
                  <label htmlFor="nombre" className={labelClasses} data-id="element-15">
                    Nombre Completo *
                  </label>
                  <input type="text" id="nombre" name="nombre" required value={formData.nombre} onChange={handleChange} placeholder="Tu nombre completo" className={inputClasses} data-id="element-16" />
                </div>
                <div data-id="element-17">
                  <label htmlFor="email" className={labelClasses} data-id="element-18">
                    Email *
                  </label>
                  <input type="email" id="email" name="email" required value={formData.email} onChange={handleChange} placeholder="tu@email.com" className={inputClasses} data-id="element-19" />
                </div>
              </div>

              {/* Row 2: Teléfono + Salón */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-20">
                <div data-id="element-21">
                  <label htmlFor="telefono" className={labelClasses} data-id="element-22">
                    Teléfono *
                  </label>
                  <input type="tel" id="telefono" name="telefono" required value={formData.telefono} onChange={handleChange} placeholder="(000) 000-0000" className={inputClasses} data-id="element-23" />
                </div>
                <div data-id="element-24">
                  <label htmlFor="salon" className={labelClasses} data-id="element-25">
                    Nombre del Salón / Negocio *
                  </label>
                  <input type="text" id="salon" name="salon" required value={formData.salon} onChange={handleChange} placeholder="Nombre de tu salón" className={inputClasses} data-id="element-26" />
                </div>
              </div>

              {/* Row 3: Instagram + Website */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-27">
                <div data-id="element-28">
                  <label htmlFor="instagram" className={labelClasses} data-id="element-29">
                    Instagram del Salón
                  </label>
                  <input type="text" id="instagram" name="instagram" value={formData.instagram} onChange={handleChange} placeholder="@tusalon" className={inputClasses} data-id="element-30" />
                </div>
                <div data-id="element-31">
                  <label htmlFor="website" className={labelClasses} data-id="element-32">
                    Website
                  </label>
                  <input type="text" id="website" name="website" value={formData.website} onChange={handleChange} placeholder="www.tusitio.com" className={inputClasses} data-id="element-33" />
                </div>
              </div>

              {/* Row 4: Clientes semana + Meta */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-34">
                <div data-id="element-35">
                  <label htmlFor="clientesSemana" className={labelClasses} data-id="element-36">
                    ¿Cuántos clientes atiendes por semana?
                  </label>
                  <select id="clientesSemana" name="clientesSemana" value={formData.clientesSemana} onChange={handleChange} className={selectClasses} data-id="element-37">
                    <option value="" disabled data-id="element-38">
                      Selecciona una opción
                    </option>
                    <option value="0-10" data-id="element-39">0 - 10</option>
                    <option value="10-25" data-id="element-40">10 - 25</option>
                    <option value="25-50" data-id="element-41">25 - 50</option>
                    <option value="50-100" data-id="element-42">50 - 100</option>
                    <option value="100+" data-id="element-43">100+</option>
                  </select>
                </div>
                <div data-id="element-44">
                  <label htmlFor="metaClientes" className={labelClasses} data-id="element-45">
                    ¿Cuál es tu meta de clientes nuevos por semana?
                  </label>
                  <select id="metaClientes" name="metaClientes" value={formData.metaClientes} onChange={handleChange} className={selectClasses} data-id="element-46">
                    <option value="" disabled data-id="element-47">
                      Selecciona una opción
                    </option>
                    <option value="5-10" data-id="element-48">5 - 10</option>
                    <option value="10-20" data-id="element-49">10 - 20</option>
                    <option value="20-50" data-id="element-50">20 - 50</option>
                    <option value="50+" data-id="element-51">50+</option>
                  </select>
                </div>
              </div>

              {/* Row 5: Publicidad + Inversión */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-52">
                <div data-id="element-53">
                  <label htmlFor="publicidadAntes" className={labelClasses} data-id="element-54">
                    ¿Has hecho publicidad digital antes?
                  </label>
                  <select id="publicidadAntes" name="publicidadAntes" value={formData.publicidadAntes} onChange={handleChange} className={selectClasses} data-id="element-55">
                    <option value="" disabled data-id="element-56">
                      Selecciona una opción
                    </option>
                    <option value="si" data-id="element-57">Sí</option>
                    <option value="no" data-id="element-58">No</option>
                    <option value="sin-resultados" data-id="element-59">
                      Un poco pero sin resultados
                    </option>
                  </select>
                </div>
                <div data-id="element-60">
                  <label htmlFor="inversionMensual" className={labelClasses} data-id="element-61">
                    ¿Cuánto has invertido en publicidad mensualmente?
                  </label>
                  <select id="inversionMensual" name="inversionMensual" value={formData.inversionMensual} onChange={handleChange} className={selectClasses} data-id="element-62">
                    <option value="" disabled data-id="element-63">
                      Selecciona una opción
                    </option>
                    <option value="nada" data-id="element-64">Nada</option>
                    <option value="menos-500" data-id="element-65">Menos de $500</option>
                    <option value="500-1000" data-id="element-66">$500 - $1,000</option>
                    <option value="1000-3000" data-id="element-67">$1,000 - $3,000</option>
                    <option value="mas-3000" data-id="element-68">Más de $3,000</option>
                  </select>
                </div>
              </div>

              {/* Error message */}
              {status === 'error' && <div className="flex items-center gap-2 text-red-400 text-sm bg-red-400/10 border border-red-400/20 rounded-lg p-3" data-id="element-69">
                  <AlertCircleIcon className="w-5 h-5 shrink-0" data-id="element-70" />
                  <span data-id="element-71">
                    Hubo un error al enviar. Por favor intenta de nuevo o
                    contáctanos directamente.
                  </span>
                </div>}

              {/* Submit */}
              <motion.button type="submit" disabled={status === 'sending'} whileHover={{
            scale: 1.02,
            boxShadow: '0px 0px 30px rgba(242, 183, 5, 0.3)'
          }} whileTap={{
            scale: 0.98
          }} className="w-full bg-gold text-carbon font-bold py-4 px-8 rounded-lg text-base md:text-lg flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer" data-id="element-72">
                {status === 'sending' ? <>
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" data-id="element-73">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" data-id="element-74" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" data-id="element-75" />
                    </svg>
                    ENVIANDO...
                  </> : <>
                    <SendIcon className="w-5 h-5" data-id="element-76" />
                    SOLICITAR MI DIAGNÓSTICO GRATUITO
                  </>}
              </motion.button>

              {/* Calendar link below */}
              <p className="text-center text-gray-500 text-xs md:text-sm pt-1" data-id="element-77">
                ¿Prefieres agendar directamente?{' '}
                <a href="https://calendar.app.google/PYPdcRoYne7QrNJA7" target="_blank" rel="noopener noreferrer" className="text-gold underline underline-offset-2 hover:text-gold/80 transition-colors" data-id="element-78">
                  Reserva en nuestro calendario →
                </a>
              </p>
            </form>}
        </motion.div>
      </div>
    </section>;
}